import React, { FC } from 'react';

import { Box, Grid, Stack } from '@mui/material';
import { useFormik } from 'formik';

import {
  IAdminRequestDetailsContractModel,
  IAdminWorkshiftDetails,
  ILongContractRequestResponse,
  ILongContractRequestsData,
  ILongTermContractModel,
} from '@pharmaplan/common';
import { stub } from '@pharmaplan/common/helpers/utils';

import useGetLongTermDistributions from '../../../../hooks/PharmacistProfile/useGetLongTermDistributions';
import ShiftsDistributionItem from '../LongShiftsDistribution/ShiftsDistributionItem';
import HourlyRateChipLabel from '../../../common/AdminHourlyRateFooter/HourlyRateChipLabel';
import { ChipType, Constants } from '../../../../helpers/Constants';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { resetDialog } from '../../../../reducers/dialogReducer';
import LongContractCard from '../../../common/LongContractCard';
import CustomTextField from '../../../common/CustomTextField';
import genericClasses from '../../../../theme/GenericClasses';
import CustomButton from '../../../common/CustomButton';
import strings from '../../../../localization';

import styles from './styles';

const { hourlyRate } = ChipType;

interface ILongShiftDistributionModal {
  details:
    | ILongContractRequestResponse
    | (IAdminRequestDetailsContractModel & { name: string })
    | (IAdminWorkshiftDetails & {
        requests: Array<ILongContractRequestsData>;
        counterRequests?: Array<ILongContractRequestsData>;
        counterShiftContract?: ILongTermContractModel;
        logoImage: string;
      });
  successAction: string;
}

const LongShiftDistributionModal: FC<ILongShiftDistributionModal> = ({
  details,
  successAction,
}) => {
  const dispatch = useAppDispatch();

  const {
    requests,
    counterRequests,
    longTermShiftContract,
    counterShiftContract,
  } = details ?? {};

  const { totalShifts } = useGetLongTermDistributions(
    requests,
    counterRequests,
  );

  const { allowanceNotes, weekDayPaidHourlyRate, weekendPaidHourlyRate } = longTermShiftContract ?? {};
  const {
    allowanceNotes: proposedAllowance,
    weekDayPaidHourlyRate: counterWeekdayRate,
    weekendPaidHourlyRate: counterWeekendRate,
  } = counterShiftContract ?? {};

  const formik = useFormik({
    initialValues: {
      allowanceNotes,
      proposedAllowance,
      weekDayPaidHourlyRate: counterWeekdayRate ?? weekDayPaidHourlyRate,
      weekendPaidHourlyRate: counterWeekendRate ?? weekendPaidHourlyRate,
    },
    enableReinitialize: true,
    onSubmit: stub,
  });

  const handleClose = () => {
    dispatch(resetDialog());
  };

  const showWeekdayStrike = counterWeekendRate && counterWeekendRate !== weekendPaidHourlyRate;
  const showWeekendStrike = counterWeekendRate && counterWeekendRate !== weekendPaidHourlyRate;

  const showWeekday = !!(counterWeekdayRate || weekDayPaidHourlyRate);
  const showWeekend = !!(counterWeekendRate || weekendPaidHourlyRate);

  return (
    <Box sx={styles.container}>
      <Box sx={styles.subContainer}>
        <Grid container columnSpacing="30px">
          <Grid md={5} xs={12} item>
            <Stack>
              <Box sx={{ mb: '24px' }}>
                <LongContractCard
                  successAction={successAction}
                  event={details}
                  formik={formik}
                />
              </Box>

              {proposedAllowance && (
                <CustomTextField
                  formik={formik}
                  placeholder={' '}
                  label={strings.proposedAllowances}
                  name="proposedAllowance"
                  multiline
                  maxLength={200}
                  disabled
                  customClass={genericClasses.borderNoneTextField}
                />
              )}
            </Stack>
          </Grid>
          <Grid md={7} xs={12} item>
            <Box sx={[styles.chips]}>
              {showWeekday && (
                <HourlyRateChipLabel
                  customContainerStyle={styles.chipContainer}
                  customLabelStyle={styles.chipLabel}
                  type={hourlyRate}
                  label={strings.weekdayHourlyRate}
                  rate={counterWeekdayRate ?? weekDayPaidHourlyRate}
                  successAction={successAction}
                  strikeRate={showWeekdayStrike ? weekDayPaidHourlyRate : null}
                />
              )}

              {showWeekend && (
                <HourlyRateChipLabel
                  customContainerStyle={styles.chipContainer}
                  customLabelStyle={styles.chipLabel}
                  type={hourlyRate}
                  label={strings.weekendHourlyRate}
                  rate={counterWeekendRate ?? weekendPaidHourlyRate}
                  successAction={successAction}
                  strikeRate={showWeekendStrike ? weekendPaidHourlyRate : null}
                />
              )}
            </Box>
            <Box sx={styles.grid}>
              <ShiftsDistributionItem totalShifts={totalShifts} />
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Box sx={[styles.buttonContainer, genericClasses.flex]}>
        <CustomButton
          label={strings.close}
          onClick={handleClose}
          variant={Constants.variant.contained}
          customClass={styles.closeButton}
        />
      </Box>
    </Box>
  );
};

export default LongShiftDistributionModal;
